import React, { useState, useRef, useEffect, useMemo } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { getAuth } from "firebase/auth";
import { getChatHistory, saveChatHistory } from "./chatMemory.jsx";
import "./Profile.css";
import { Link } from "react-router-dom";

function saveStudentProfile(profile, countryCode) {
  try {
    const uid = getAuth().currentUser?.uid;
    const key = uid ? `studentProfile_${uid}` : "studentProfile_guest";
    localStorage.setItem(key, JSON.stringify({ ...profile, country: countryCode }));
  } catch (e) { /* ignore */ }
}

// ── API setup ──────────────────────────────────────────────────────────────────
const GEMINI_KEY = "AIzaSyD_cIDIcJR3OolY3piQxozRG4Zccr2thPk";
const OPENAI_KEY = "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";

const genAI = new GoogleGenerativeAI(GEMINI_KEY);
const geminiModel = genAI.getGenerativeModel({
  model: "gemini-2.5-flash-lite",
  generationConfig: { temperature: 1, topP: 0.95, topK: 64, maxOutputTokens: 1200, responseMimeType: "text/plain" },
});

// ── Country config ─────────────────────────────────────────────────────────────
const COUNTRIES = [
  { code: "IN", flag: "🇮🇳", name: "India" },
  { code: "CN", flag: "🇨🇳", name: "China" },
  { code: "CA", flag: "🇨🇦", name: "Canada" },
  { code: "MX", flag: "🇲🇽", name: "Mexico" },
  { code: "PK", flag: "🇵🇰", name: "Pakistan" },
  { code: "KR", flag: "🇰🇷", name: "South Korea" },
  { code: "AE", flag: "🇦🇪", name: "UAE" },
  { code: "BR", flag: "🇧🇷", name: "Brazil" },
  { code: "NG", flag: "🇳🇬", name: "Nigeria" },
  { code: "GB", flag: "🇬🇧", name: "United Kingdom" },
  { code: "XX", flag: "🌍", name: "Other Country" },
];

const COUNTRY_CONFIG = {
  IN: {
    gradeQ: "What is your percentage or CGPA? (CBSE / ICSE / State Board / IB / IGCSE)",
    gradeHint: "e.g., 92%, 9.4 CGPA, 38/45 IB",
    englishQ: "What is your IELTS, TOEFL, or Duolingo score?",
    englishHint: "e.g., IELTS 7.0, TOEFL 105, Duolingo 120",
    context: `The student is from India. Key context for the analysis:
- Indian board percentages (CBSE/ICSE/State) are not GPA — colleges contextualize them. 90%+ on CBSE is very competitive.
- Mention specifically how their score compares in the Indian context (CBSE, ICSE, or State Board).
- Strong STEM orientation is common among Indian applicants; differentiation through research, Olympiads, or unique ECs matters.
- Note the importance of financial aid / scholarship awareness for Indian students.
- Visa: F-1 student visa required.`,
  },
  CN: {
    gradeQ: "What is your Gaokao score or high school percentage?",
    gradeHint: "e.g., 620/750 Gaokao, 95% high school average",
    englishQ: "What is your TOEFL or IELTS score?",
    englishHint: "e.g., TOEFL 110, IELTS 7.5",
    context: `The student is from China. Key context:
- Gaokao scores are evaluated relative to provincial averages — mention this explicitly.
- Strong academic performance is expected; research, competition awards (AMC, physics olympiad) differentiate applicants.
- Essays in English are critical; authenticity and personal narrative carry extra weight.
- Visa: F-1 student visa required. Financial documentation is important.`,
  },
  CA: {
    gradeQ: "What is your percentage or letter grade? (provincial curriculum)",
    gradeHint: "e.g., 91%, A average, 4.0 GPA",
    englishQ: "English proficiency test score (most US schools don't require one for Canadians — enter N/A if not taken)",
    englishHint: "e.g., N/A, TOEFL 108, IELTS 7.5",
    context: `The student is from Canada. Key context:
- Most US universities do not require English proficiency tests for Canadian applicants — note this if they entered N/A.
- Ontario and BC curricula are well-recognized. Quebec CEGEP students have a slightly different profile.
- SAT/ACT is optional at many schools but recommended for merit scholarships.
- Canadian students are often considered domestic or international depending on the institution — mention this nuance.`,
  },
  MX: {
    gradeQ: "What is your grade on the Mexican 0–10 scale or percentage?",
    gradeHint: "e.g., 9.3/10, 93%",
    englishQ: "What is your TOEFL, IELTS, or Duolingo score?",
    englishHint: "e.g., TOEFL 100, IELTS 7.0, Duolingo 115",
    context: `The student is from Mexico. Key context:
- Grades on a 10-point scale; 9+ is excellent. Contextualize accordingly.
- Bilingual students or those from international / bilingual schools have an advantage.
- SAT/ACT is commonly required. Some border-region schools may have specific recruitment for Mexican students.
- Visa: F-1 student visa required.`,
  },
  PK: {
    gradeQ: "What are your O/A Level grades, Matric/FSc percentage, or IB score?",
    gradeHint: "e.g., A* in 9 subjects (O Levels), 88% FSc, 37/45 IB",
    englishQ: "What is your IELTS, TOEFL, or Duolingo score?",
    englishHint: "e.g., IELTS 7.0, TOEFL 105",
    context: `The student is from Pakistan. Key context:
- O/A Level Cambridge results are very well-recognized by US colleges; A/A* grades are competitive.
- FSc percentages are less globally standardized — provide context on typical ranges.
- SAT is commonly taken; 1350+ is competitive for many mid-tier schools.
- Strong academic narrative + financial aid planning is critical — mention scholarship resources.
- Visa: F-1 student visa required.`,
  },
  KR: {
    gradeQ: "What is your Suneung (CSAT) score or high school GPA?",
    gradeHint: "e.g., 1등급 (Grade 1 CSAT), 4.3/4.5 GPA, 95%",
    englishQ: "What is your TOEFL or IELTS score?",
    englishHint: "e.g., TOEFL 108, IELTS 7.5",
    context: `The student is from South Korea. Key context:
- Suneung (CSAT) grades (1–9, where 1 is best) should be contextualized; mention that Grade 1 represents top 4%.
- Strong performance in competitions (KMO, science olympiads) is highly valued.
- Research internships and independent projects differentiate Korean applicants.
- Visa: F-1 student visa required.`,
  },
  AE: {
    gradeQ: "What is your percentage or IB / A-Level / American curriculum grade?",
    gradeHint: "e.g., 96%, 42/45 IB, A* in 4 A-Levels, 4.0 GPA",
    englishQ: "What is your IELTS, TOEFL, or Duolingo score? (may be waived for students from English-curriculum schools)",
    englishHint: "e.g., IELTS 7.5, N/A (American curriculum)",
    context: `The student is from the UAE. Key context:
- The UAE has a highly diverse international school system (IB, British A-Levels, American, Indian CBSE).
- Curriculum type strongly affects how grades are evaluated — tailor advice accordingly.
- IB diploma holders have a well-recognized credential globally.
- Visa: F-1 student visa required.`,
  },
  BR: {
    gradeQ: "What is your grade on Brazil's 0–10 scale, ENEM score, or IB score?",
    gradeHint: "e.g., 9.5/10, ENEM 780/1000, 38/45 IB",
    englishQ: "What is your TOEFL, IELTS, or Duolingo score?",
    englishHint: "e.g., TOEFL 100, IELTS 7.0, Duolingo 115",
    context: `The student is from Brazil. Key context:
- Brazilian grades on 0–10; 9+ is excellent. ENEM scores can be mentioned but are not universally recognized by US colleges.
- IB diploma holders have a significant advantage.
- Research experience and competition awards are valued.
- Visa: F-1 student visa required. Financial documentation is important.`,
  },
  NG: {
    gradeQ: "What are your WAEC/NECO results or A-Level / IB scores?",
    gradeHint: "e.g., 8 A1s (WAEC), 40/45 IB, A* A* A (A-Levels)",
    englishQ: "What is your IELTS, TOEFL, or Duolingo score? (may be waived for some)",
    englishHint: "e.g., IELTS 7.0, TOEFL 105",
    context: `The student is from Nigeria. Key context:
- WAEC/NECO A1 grades are recognized but less globally standardized — provide context.
- A-Level / IB credentials are very well-recognized and prestigious.
- Strong ECs, community leadership, and essays carry significant weight.
- Many Nigerian students pursue scholarships; mention relevant resources.
- Visa: F-1 student visa required.`,
  },
  GB: {
    gradeQ: "What are your A-Level predicted/actual grades or IB score?",
    gradeHint: "e.g., A*A*A A-Levels, 42/45 IB, 4.0 GPA (if American curriculum abroad)",
    englishQ: "English proficiency (usually waived for UK students — enter N/A if applicable)",
    englishHint: "e.g., N/A, IELTS 7.5",
    context: `The student is from the United Kingdom. Key context:
- A-Level grades (A*, A, B) are very well-recognized by US colleges; 3 A-Levels at A*/A is highly competitive.
- IB is equally well-recognized.
- UCAS and US applications are different processes — note key differences (essays, SAT, financial aid) if relevant.
- Visa: F-1 student visa required (UK citizens are not exempt).`,
  },
  XX: {
    gradeQ: "What is your grade average, percentage, or IB/A-Level score? (include your grading system)",
    gradeHint: "e.g., 90%, 3.9/4.0, 38/45 IB, A* in 4 subjects",
    englishQ: "What is your IELTS, TOEFL, or Duolingo score?",
    englishHint: "e.g., IELTS 7.0, TOEFL 100, Duolingo 120",
    context: "The student is an international student. Provide general advice relevant to international applicants to US colleges.",
  },
};

// ── File parsing ───────────────────────────────────────────────────────────────
const MAX_TEXT_CHARS = 200_000;
const ACCEPTED_TYPES = ".txt,.md,.csv,.json,.pdf,.docx";

async function parsePDF(file) {
  try {
    const pdfjsLib = await import("pdfjs-dist");
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let text = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map(item => item.str).join(" ") + "\n";
    }
    return text;
  } catch (err) {
    throw new Error(`PDF parse error: ${err.message}`);
  }
}

async function parseDocx(file) {
  try {
    const mammoth = await import("mammoth");
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  } catch (err) {
    throw new Error(`DOCX parse error: ${err.message}`);
  }
}

async function fileToText(file) {
  const ext = file.name.split(".").pop().toLowerCase();
  try {
    if (ext === "pdf")  return { name: file.name, text: await parsePDF(file) };
    if (ext === "docx") return { name: file.name, text: await parseDocx(file) };
    // Text-based files
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve({ name: file.name, text: reader.result || "" });
      reader.onerror = () => resolve({ name: file.name, text: "" });
      reader.readAsText(file);
    });
  } catch (err) {
    return { name: file.name, text: "", error: err.message };
  }
}

// ── OpenAI helper ──────────────────────────────────────────────────────────────
async function askOpenAI(prompt, model = "gpt-3.5-turbo") {
  if (!OPENAI_KEY) throw new Error("Missing REACT_APP_OPENAI_API_KEY.");
  const resp = await fetch("https://api.openai.com/v1/chat/completions", {
    method: "POST",
    headers: { Authorization: `Bearer ${OPENAI_KEY}`, "Content-Type": "application/json" },
    body: JSON.stringify({ model, messages: [{ role: "user", content: prompt }], temperature: 1 }),
  });
  if (!resp.ok) throw new Error(`OpenAI ${resp.status}: ${await resp.text().catch(() => resp.statusText)}`);
  const data = await resp.json();
  const content = data?.choices?.[0]?.message?.content;
  if (!content) throw new Error("OpenAI returned no content.");
  return content;
}

// ── Render AI response safely (no dangerouslySetInnerHTML) ─────────────────────
function AIResponse({ text }) {
  const lines = text.split("\n");
  return (
    <div className="ai-response">
      {lines.map((line, i) => {
        const clean = line.replace(/\*\*/g, "").replace(/^#+\s*/, "").trim();
        if (!clean) return <br key={i} />;
        if (clean.match(/^[-•*]\s+/)) {
          return <li key={i}>{clean.replace(/^[-•*]\s+/, "")}</li>;
        }
        if (clean.match(/^\d+\.\s+/)) {
          return <li key={i}>{clean.replace(/^\d+\.\s+/, "")}</li>;
        }
        return <p key={i}>{clean}</p>;
      })}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
const ProfileBuilder = () => {
  // Country selection (step 0)
  const [selectedCountry, setSelectedCountry] = useState(null);

  const [userInput,    setUserInput]    = useState("");
  const [chatHistory,  setChatHistory]  = useState([]);
  const [isLoading,    setIsLoading]    = useState(false);
  const [isBotTyping,  setIsBotTyping]  = useState(false);
  const [modelChoice,  setModelChoice]  = useState("gemini");
  const [hasAnalyzed,  setHasAnalyzed]  = useState(false);

  const [selectedFiles,  setSelectedFiles]  = useState([]);
  const [fileText,       setFileText]       = useState("");
  const [isParsingFiles, setIsParsingFiles] = useState(false);

  const chatWindowRef = useRef(null);

  const countryConfig = selectedCountry ? COUNTRY_CONFIG[selectedCountry.code] : null;

  const questionOrder = useMemo(() => {
    if (!countryConfig) return [];
    return [
      "What is your SAT score or ACT score?",
      countryConfig.gradeQ,
      countryConfig.englishQ,
      "Describe your volunteering experience.",
      "What extracurricular activities have you participated in?",
      "What is your intended major?",
    ];
  }, [countryConfig]);

  const [questionIndex, setQuestionIndex] = useState(0);
  const [userProfile,   setUserProfile]   = useState({});

  // Start questions once country is chosen
  useEffect(() => {
    if (!selectedCountry) return;
    const saved = getChatHistory();
    if (saved.length > 0) {
      setChatHistory(saved);
      // Restore session state from saved history
      const alreadyAnalyzed = saved.some(m => m.isAI === true);
      if (alreadyAnalyzed) {
        // Analysis was completed — restore profile data and treat future inputs as follow-ups
        setHasAnalyzed(true);
        setQuestionIndex(questionOrder.length);
        try {
          const uid = getAuth().currentUser?.uid;
          const key = uid ? `studentProfile_${uid}` : 'studentProfile_guest';
          const raw = localStorage.getItem(key);
          if (raw) setUserProfile(JSON.parse(raw));
        } catch {}
      } else {
        // Partially answered — resume from where they left off
        const answeredCount = saved.filter(m => m.user && !m.message?.startsWith('📎')).length;
        setQuestionIndex(Math.min(answeredCount + 1, questionOrder.length));
      }
    } else {
      setChatHistory([
        { user: false, message: `Welcome! Since you're from ${selectedCountry.flag} ${selectedCountry.name}, I've tailored the questions to your background.` },
        { user: false, message: "Let's build your profile. Please answer the following questions:" },
      ]);
      setQuestionIndex(0);
      // Ask Q1 after brief delay for UX
      setTimeout(() => {
        setChatHistory(prev => [...prev, { user: false, message: questionOrder[0] }]);
        setQuestionIndex(1);
      }, 400);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  useEffect(() => { saveChatHistory(chatHistory); }, [chatHistory]);

  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [chatHistory, isBotTyping]);

  const askNextQuestion = (idx) => {
    if (idx < questionOrder.length) {
      setChatHistory(prev => [...prev, { user: false, message: questionOrder[idx] }]);
      setQuestionIndex(idx + 1);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSelectedFiles(files);
    setChatHistory(prev => [...prev, {
      user: true,
      message: `📎 Attached: ${files.map(f => f.name).join(", ")}`,
    }]);
    setIsParsingFiles(true);
    try {
      const results = await Promise.all(files.map(fileToText));
      const errors  = results.filter(r => r.error).map(r => `${r.name}: ${r.error}`);
      const combined = results.map(r => `--- ${r.name} ---\n${r.text}`).join("\n\n").slice(0, MAX_TEXT_CHARS);
      setFileText(combined);
      // Warn if a PDF extracted almost nothing (likely image-based / scanned)
      const imagePdfs = results.filter(r => r.name.endsWith(".pdf") && r.text.trim().length < 100);
      let msg;
      if (errors.length) {
        msg = `⚠️ Processed ${files.length - errors.length}/${files.length} files (${errors.join("; ")}). Content loaded.`;
      } else if (imagePdfs.length) {
        msg = `⚠️ PDF appears to be image-based (scanned) — only ${combined.length} characters extracted. For best results, upload a text-based PDF, DOCX, or TXT file.`;
      } else {
        msg = `✅ Processed ${files.length} file(s) (${combined.length.toLocaleString()} chars). Content loaded.`;
      }
      setChatHistory(prev => [...prev, { user: false, message: msg }]);
    } catch (err) {
      setChatHistory(prev => [...prev, { user: false, message: `⚠️ File error: ${err.message}` }]);
    } finally {
      setIsParsingFiles(false);
    }
  };

  const buildFinalPrompt = (profile) => {
    const cc = countryConfig || COUNTRY_CONFIG["XX"];
    const fileSection = fileText
      ? `\n\n--- DOCUMENT TEXT (extracted inline from user's uploaded file) ---\n${fileText}\n--- END DOCUMENT TEXT ---\n`
      : "";
    return (
      `You are a college admissions counselor specializing in international students applying to US universities.\n\n` +
      `${cc.context}\n\n` +
      `The student has provided the following profile details:\n${JSON.stringify(profile, null, 2)}\n` +
      fileSection +
      `\nAnalyze this student's profile strength for US college admissions. Be specific to their country background. ` +
      `Provide:\n1. Overall profile assessment\n2. Strengths\n3. Areas for improvement\n4. Specific action items\n` +
      (fileText ? `Also incorporate any relevant information from the document text provided above.\n` : "") +
      `Avoid GPA references (not standard for this student's background). ` +
      `Keep advice practical and actionable. Use plain text formatting.`
    );
  };

  const buildFollowUpPrompt = (question, profile) => {
    const cc = countryConfig || COUNTRY_CONFIG["XX"];
    const fileSection = fileText
      ? `\n\nThe following text was extracted directly from the student's uploaded document — it is already available to you inline, no external access needed:\n--- DOCUMENT START ---\n${fileText}\n--- DOCUMENT END ---\n`
      : "";
    return (
      `You are a college admissions counselor. ${cc.context}\n\n` +
      `Student profile: ${JSON.stringify(profile)}\n` +
      fileSection +
      `\nThe student asks: "${question}"\n\n` +
      (fileText
        ? `If this question is about the uploaded document, answer using the document text provided above (between the DOCUMENT START/END markers). Do not say you cannot access files — the content is already extracted and included above.`
        : `Answer concisely and specifically.`)
    );
  };

  const sendMessage = async () => {
    if (!userInput.trim() || isParsingFiles) return;
    const response = userInput.trim();
    setChatHistory(prev => [...prev, { user: true, message: response }]);
    setUserInput("");
    setIsLoading(true);
    setIsBotTyping(true);

    // Map response to profile key
    const currentQ = questionOrder[questionIndex - 1] || "";
    const profileUpdate = {};
    if (currentQ.includes("SAT") || currentQ.includes("ACT"))           profileUpdate.testScores = response;
    else if (currentQ === countryConfig?.gradeQ)                        profileUpdate.academicRecord = response;
    else if (currentQ === countryConfig?.englishQ)                      profileUpdate.englishProficiency = response;
    else if (currentQ.includes("volunteering"))                          profileUpdate.volunteering = response;
    else if (currentQ.includes("extracurricular"))                       profileUpdate.extracurriculars = response;
    else if (currentQ.includes("major"))                                 profileUpdate.intendedMajor = response;

    const merged = { ...userProfile, ...profileUpdate };
    setUserProfile(merged);

    // Still have questions left
    if (questionIndex < questionOrder.length) {
      askNextQuestion(questionIndex);
      setIsLoading(false);
      setIsBotTyping(false);
      return;
    }

    // All questions answered → generate analysis
    try {
      const prompt = buildFinalPrompt(merged);
      let aiText;
      if (modelChoice === "gpt-3.5-turbo") {
        try {
          aiText = await askOpenAI(prompt);
        } catch (e) {
          setChatHistory(prev => [...prev, { user: false, message: `ℹ️ GPT-3.5 unavailable (${e.message}). Falling back to Gemini…` }]);
          const res = await geminiModel.generateContent(prompt);
          aiText = await res.response.text();
        }
      } else {
        const res = await geminiModel.generateContent(prompt);
        aiText = await res.response.text();
      }
      setChatHistory(prev => [...prev, { user: false, message: aiText, isAI: true }]);
      setHasAnalyzed(true);
      saveStudentProfile(merged, selectedCountry?.code);
    } catch (err) {
      setChatHistory(prev => [...prev, { user: false, message: `⚠️ Error: ${err.message}` }]);
    } finally {
      setIsLoading(false);
      setIsBotTyping(false);
    }
  };

  const handleFollowUp = async () => {
    if (!userInput.trim() || isParsingFiles) return;
    const q = userInput.trim();
    setChatHistory(prev => [...prev, { user: true, message: q }]);
    setUserInput("");
    setIsLoading(true);
    setIsBotTyping(true);
    try {
      const prompt = buildFollowUpPrompt(q, userProfile);
      let aiText;
      if (modelChoice === "gpt-3.5-turbo") {
        try { aiText = await askOpenAI(prompt); }
        catch (e) {
          const res = await geminiModel.generateContent(prompt);
          aiText = await res.response.text();
        }
      } else {
        const res = await geminiModel.generateContent(prompt);
        aiText = await res.response.text();
      }
      setChatHistory(prev => [...prev, { user: false, message: aiText, isAI: true }]);
    } catch (err) {
      setChatHistory(prev => [...prev, { user: false, message: `⚠️ ${err.message}` }]);
    } finally {
      setIsLoading(false);
      setIsBotTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      hasAnalyzed ? handleFollowUp() : sendMessage();
    }
  };

  const handleReset = () => {
    setChatHistory([]); setUserInput(""); setQuestionIndex(0);
    setUserProfile({}); setHasAnalyzed(false);
    setSelectedFiles([]); setFileText("");
    setSelectedCountry(null);
    saveChatHistory([]);
  };

  // ── Progress bar ──
  const progressPct = questionOrder.length > 0
    ? Math.min(100, Math.round((Math.min(questionIndex, questionOrder.length) / questionOrder.length) * 100))
    : 0;

  // ── COUNTRY SELECTION SCREEN ───────────────────────────────────────────────
  if (!selectedCountry) {
    return (
      <div className="pb-country-screen">
        <div className="pb-country-card">
          <h1 className="pb-country-title">Profile Builder</h1>
          <p className="pb-country-subtitle">
            Select your home country for a personalized analysis tailored to your background, grading system, and admissions context.
          </p>
          <div className="pb-country-grid">
            {COUNTRIES.map(c => (
              <button
                key={c.code}
                className="pb-country-btn"
                onClick={() => setSelectedCountry(c)}
              >
                <span className="pb-country-flag">{c.flag}</span>
                <span className="pb-country-name">{c.name}</span>
              </button>
            ))}
          </div>
          <div className="pb-country-footer">
            <Link to="/home" className="pb-country-back">← Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── CHAT INTERFACE ─────────────────────────────────────────────────────────
  return (
    <div className="pb-page">
      <div className="pb-interface">

        {/* Header */}
        <div className="pb-header">
          <div className="pb-header-left">
            <span className="pb-country-flag-sm">{selectedCountry.flag}</span>
            <div>
              <h2 className="pb-header-title">Profile Builder</h2>
              <span className="pb-header-sub">{selectedCountry.name} · {modelChoice === 'gemini' ? 'Gemini 2.5 Flash' : 'GPT-3.5 Turbo'}</span>
            </div>
          </div>
          <div className="pb-header-right">
            <select
              className="pb-model-select"
              value={modelChoice}
              onChange={e => setModelChoice(e.target.value)}
            >
              <option value="gemini">Gemini 2.5 Flash-Lite</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            </select>
            <button className="pb-reset-btn" onClick={handleReset} title="Start over">↺</button>
          </div>
        </div>

        {/* Progress bar */}
        {!hasAnalyzed && questionOrder.length > 0 && (
          <div className="pb-progress-wrap">
            <div className="pb-progress-track">
              <div className="pb-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <span className="pb-progress-label">
              {questionIndex <= questionOrder.length
                ? `Question ${Math.min(questionIndex, questionOrder.length)} of ${questionOrder.length}`
                : "Analysis complete"}
            </span>
          </div>
        )}

        {/* Chat window */}
        <div className="pb-chat-window" ref={chatWindowRef}>
          {chatHistory.map((msg, i) => (
            <div key={i} className={`pb-msg ${msg.user ? "pb-msg--user" : "pb-msg--bot"}`}>
              {!msg.user && (
                <div className="pb-msg-avatar">{selectedCountry.flag}</div>
              )}
              <div className="pb-msg-bubble">
                {msg.isAI
                  ? <AIResponse text={msg.message} />
                  : <span>{msg.message}</span>
                }
              </div>
            </div>
          ))}
          {isParsingFiles && (
            <div className="pb-msg pb-msg--bot">
              <div className="pb-msg-avatar">⏳</div>
              <div className="pb-msg-bubble"><span>Processing your files…</span></div>
            </div>
          )}
          {isBotTyping && (
            <div className="pb-msg pb-msg--bot">
              <div className="pb-msg-avatar">{selectedCountry.flag}</div>
              <div className="pb-msg-bubble pb-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="pb-input-area">
          <textarea
            className="pb-textarea"
            placeholder={hasAnalyzed ? "Ask a follow-up question…" : "Type your answer…"}
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isParsingFiles || isLoading}
            rows={2}
          />
          <div className="pb-input-row">
            <label className="pb-file-label" title="Attach files (PDF, DOCX, TXT, CSV, JSON)">
              📎
              <input
                type="file"
                multiple
                accept={ACCEPTED_TYPES}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </label>
            {selectedFiles.length > 0 && (
              <span className="pb-file-count">{selectedFiles.length} file(s)</span>
            )}
            <div className="pb-spacer" />
            <Link to="/home">
              <button className="pb-btn pb-btn--ghost">Home</button>
            </Link>
            <button
              className="pb-btn pb-btn--primary"
              onClick={hasAnalyzed ? handleFollowUp : sendMessage}
              disabled={isLoading || isParsingFiles || !userInput.trim()}
            >
              {isLoading ? "…" : "Send"}
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="pb-disclaimer">
          This is a student-built tool for educational guidance only. For official advice, consult a certified college counselor.
        </p>
      </div>
    </div>
  );
};

export default ProfileBuilder;
